import { LayoutGrid, List } from 'lucide-react'
import useSegmentedTabIndicator from '../../hooks/useSegmentedTabIndicator'

const OPTIONS = [
  { key: 'list', label: 'List', Icon: List },
  { key: 'grid', label: 'Grid', Icon: LayoutGrid },
]

// List/grid switch with a pill that slides to the active option. Arrow keys
// move between options, as in any radio group.
export default function ViewModeToggle({ value, onChange, className = '' }) {
  const { tabRefs, indicatorStyle } = useSegmentedTabIndicator(value)

  const handleKeyDown = (e) => {
    const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[e.key]
    if (!step) return
    e.preventDefault()
    const index = OPTIONS.findIndex(o => o.key === value)
    const next = OPTIONS[(index + step + OPTIONS.length) % OPTIONS.length]
    onChange(next.key)
    tabRefs.current[next.key]?.focus()
  }

  return (
    <div
      role="radiogroup"
      aria-label="Layout"
      onKeyDown={handleKeyDown}
      className={`relative inline-flex h-9 flex-shrink-0 items-center rounded-lg border border-gray-200 bg-gray-100/70 p-0.5 dark:border-gray-700 dark:bg-gray-800/70 ${className}`}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0.5 left-0 rounded-md bg-white shadow-sm ring-1 ring-gray-900/5 transition-[transform,width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none dark:bg-gray-900 dark:ring-white/10"
        style={{
          width: indicatorStyle.width,
          transform: `translateX(${indicatorStyle.left}px)`,
          opacity: indicatorStyle.width ? 1 : 0,
        }}
      />
      {OPTIONS.map(({ key, label, Icon }) => {
        const active = value === key
        return (
          <button
            key={key}
            ref={(el) => { tabRefs.current[key] = el }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(key)}
            title={`${label} view`}
            className={`relative z-10 inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
              active
                ? 'text-gray-900 dark:text-white'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Icon
              className={`h-4 w-4 transition-[transform,color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                active ? 'scale-110 text-navy-600 dark:text-navy-300' : ''
              }`}
            />
            {label}
          </button>
        )
      })}
    </div>
  )
}
